import { useCallback } from 'react'

import Decimal from 'decimal.js'
import { toast } from 'react-toastify'

import { BookingChargeDefaultsByType } from 'constants/EntityConstants/OtherChargeEntity'
import { BookingChargeType, WaitAndReturnFeeCategory } from 'generated/graphql'
import { TBookingChargeForm } from 'types'

type IUseWaitAndReturnChargeSyncProps = {
  feeCategories: WaitAndReturnFeeCategory[]
  isWaitAndReturn: boolean
  consignmentFee: number | undefined
  waitAndReturnFeeCategoryId: string | undefined
}

export const useWaitAndReturnChargeSync = ({
  feeCategories,
  isWaitAndReturn,
  consignmentFee,
  waitAndReturnFeeCategoryId,
}: IUseWaitAndReturnChargeSyncProps) => {
  const handleWaitAndReturnChargeRemoved = useCallback((charges: TBookingChargeForm[]) => {
    const existingWaitAndReturnCharge = charges.find(
      (x) => x?.type === BookingChargeType.WaitAndReturn,
    )

    if (!existingWaitAndReturnCharge) {
      return
    }

    charges.splice(
      0,
      charges.length,
      ...charges.filter((x) => x?.type !== BookingChargeType.WaitAndReturn),
    )
    toast.info('Automatic charge Wait & Return was removed.')
  }, [])

  const handleWaitAndReturnChargeApplied = useCallback(
    (charges: TBookingChargeForm[]) => {
      if (!feeCategories) {
        return
      }

      const selectedCategory = feeCategories.find(
        (category: any) => String(category.id) === String(waitAndReturnFeeCategoryId),
      )
      const waitAndReturnPercent = selectedCategory?.percent ?? 0

      const rate = new Decimal(consignmentFee ?? 0)
        .mul(waitAndReturnPercent)
        .div(100)
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
        .toNumber()

      const currentWaitAndReturnChargeIndex = charges.findIndex(
        (x) => x?.type === BookingChargeType.WaitAndReturn,
      )

      if (currentWaitAndReturnChargeIndex === -1) {
        const defaults = BookingChargeDefaultsByType[BookingChargeType.WaitAndReturn]
        if (!defaults) {
          return
        }

        charges.push({ ...defaults, rate })
        toast.info('Automatic charge Wait & Return was added.')
        return
      }

      const existingCharge = charges[currentWaitAndReturnChargeIndex]

      if (existingCharge.rate === rate) {
        return
      }

      charges[currentWaitAndReturnChargeIndex] = { ...existingCharge, rate, quantity: 1 }
      toast.info('Automatic charge Wait & Return was updated.')
    },
    [consignmentFee, feeCategories, waitAndReturnFeeCategoryId],
  )

  const handleWaitAndReturnChargeSync = useCallback(
    (charges: TBookingChargeForm[]) => {
      if (!isWaitAndReturn) {
        handleWaitAndReturnChargeRemoved(charges)
        return
      }

      handleWaitAndReturnChargeApplied(charges)
    },
    [handleWaitAndReturnChargeApplied, handleWaitAndReturnChargeRemoved, isWaitAndReturn],
  )

  return { handleWaitAndReturnChargeSync }
}
